// src/pages/CaseStudy/Hardware.jsx
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  LinearProgress,
} from "@mui/material";
import {
  Close,
  ArrowForward,
  CheckCircle,
  Phone,
  Email,
  Hardware,
  Devices,
  Speed,
  TrendingUp,
  EmojiEvents,
  Security,
  Build,
  Lightbulb,
  Business,
  School,
  MedicalServices,
  Wifi,
  AttachMoney,
} from "@mui/icons-material";
import styles from "./Hardware.module.css";

// Case Study Data
const caseStudies = [
  {
    id: 1,
    title: "WaveSmartPulse - Health Wearable",
    category: "Wearable Technology",
    image:
      "https://images.unsplash.com/photo-1579586337278-3befd40fd17a?w=600&h=400&fit=crop",
    icon: <Devices sx={{ fontSize: 40 }} />,
    description:
      "A computerized wristwatch with health monitoring features including heart rate, blood pressure monitor, and pedometer.",
    challenge:
      "Develop a cost-effective health monitoring device that provides accurate real-time data while being user-friendly and affordable.",
    solution:
      "Created WaveSmartPulse with dynamic health monitoring, sleep tracking, and smart notifications. Integrated with mobile apps for data visualization.",
    results:
      "Successfully launched with 95% customer satisfaction. Reduced manufacturing costs by 20% through innovative design.",
    features: [
      "Heart Rate Monitor",
      "Blood Pressure Monitor",
      "Sleep Tracking",
      "Smart Notifications",
      "Pedometer",
    ],
    stats: [
      { label: "Customer Satisfaction", value: 95, icon: <EmojiEvents /> },
      { label: "Cost Reduction", value: 20, icon: <TrendingUp /> },
      { label: "Units Sold", value: "10,000+", icon: <Speed /> },
    ],
    color: "#8cc61d",
  },
  {
    id: 2,
    title: "WaveStream Wi-Fi - Content Transmitter",
    category: "Network Infrastructure",
    image:
      "https://images.unsplash.com/photo-1544198365-f5d60b6d8190?w=600&h=400&fit=crop",
    icon: <Wifi sx={{ fontSize: 40 }} />,
    description:
      "A multi-functional content provider and transmitter with antennas and GPS, designed to transmit pre-loaded programmes and curriculums.",
    challenge:
      "Create a robust content delivery system that works without constant internet connectivity and supports multiple devices.",
    solution:
      "Developed WaveStream Wi-Fi with embedded Wi-Fi and 3G, supporting up to 50 channels with pre-loaded content accessible via web browser.",
    results:
      "Successfully deployed in educational institutions with 98% uptime. Compatible with all Android and Apple devices.",
    features: [
      "Pre-loaded Content",
      "50+ Channels",
      "GPS Enabled",
      "Multi-language",
      "Web Browser Access",
    ],
    stats: [
      { label: "Uptime", value: 98, icon: <Security /> },
      { label: "Channels Available", value: "50+", icon: <Speed /> },
      { label: "Device Compatibility", value: "100%", icon: <Devices /> },
    ],
    color: "#4a90d9",
  },
  {
    id: 3,
    title: "Wavepalm Tablets - Education Solutions",
    category: "Tablet Technology",
    image:
      "https://images.unsplash.com/photo-1561154464-82e9adf32764?w=600&h=400&fit=crop",
    icon: <Hardware sx={{ fontSize: 40 }} />,
    description:
      "Carefully designed tablets for everyday needs, integrated with bespoke software for education, medical, sports, and entertainment sectors.",
    challenge:
      "Develop a versatile tablet solution that can be customized for different sectors while maintaining performance and affordability.",
    solution:
      "Created tablets with dual/octa core processors, 2-4GB RAM, and co-branding options. Integrated with sector-specific software solutions.",
    results:
      "Successfully deployed in 50+ schools and institutions. Enhanced learning outcomes through interactive content.",
    features: [
      "Dual/Octa Core",
      "2-4GB RAM",
      "Clear LCD",
      "Wi-Fi & 4G",
      "Custom Software Integration",
    ],
    stats: [
      { label: "Schools Deployed", value: "50+", icon: <School /> },
      { label: "Satisfaction Rate", value: 92, icon: <EmojiEvents /> },
      { label: "Sectors Served", value: "4", icon: <Business /> },
    ],
    color: "#f39c12",
  },
  {
    id: 4,
    title: "WaveSolar Power Bank - Sustainable Energy",
    category: "Sustainable Technology",
    image:
      "https://images.unsplash.com/photo-1585338107529-13afc5f02586?w=600&h=400&fit=crop",
    icon: <Lightbulb sx={{ fontSize: 40 }} />,
    description:
      "A portable solar-powered power bank available in multiple colors, designed to charge any Android and iOS devices.",
    challenge:
      "Create a sustainable, portable charging solution that works efficiently in various environments.",
    solution:
      "Developed waterproof, anti-drop power bank with 5000mAh capacity and solar panel for eco-friendly charging.",
    results:
      "Reduced carbon footprint by 30% compared to traditional power banks. Received positive feedback for portability and design.",
    features: [
      "5000mAh Capacity",
      "Solar Panel",
      "Waterproof",
      "Li-polymer Battery",
      "Multiple Colors",
    ],
    stats: [
      { label: "Eco-Friendly Rating", value: 95, icon: <Lightbulb /> },
      { label: "Portability Score", value: 98, icon: <Speed /> },
      { label: "Design Versatility", value: "4 Colors", icon: <Build /> },
    ],
    color: "#e74c3c",
  },
];

// Stats Data
const statsData = [
  { number: "10+", label: "Hardware Products", icon: <Hardware /> },
  { number: "50+", label: "Clients Served", icon: <Business /> },
  { number: "4.9", label: "Quality Rating", icon: <EmojiEvents /> },
  { number: "30%", label: "Cost Reduction", icon: <TrendingUp /> },
];

// Sectors
const sectors = [
  { icon: <School />, label: "Education", color: "#8cc61d" },
  { icon: <MedicalServices />, label: "Healthcare", color: "#e74c3c" },
  { icon: <Business />, label: "Corporate", color: "#4a90d9" },
  { icon: <AttachMoney />, label: "Finance", color: "#2ecc71" },
];

const HardwareCaseStudy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);

  const handleOpenDialog = (study) => {
    setSelectedStudy(study);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStudy(null);
  };

  return (
    <Box className={styles.caseStudyPage}>
      {/* ===== HERO SECTION WITH BACKGROUND IMAGE & ANIMATIONS ===== */}
      <Box className={styles.heroSection}>
        {/* Animated Background Elements */}
        <Box className={styles.animatedBg}>
          {/* Floating Particles */}
          <Box className={`${styles.particle} ${styles.particle1}`}></Box>
          <Box className={`${styles.particle} ${styles.particle2}`}></Box>
          <Box className={`${styles.particle} ${styles.particle3}`}></Box>
          <Box className={`${styles.particle} ${styles.particle4}`}></Box>
          <Box className={`${styles.particle} ${styles.particle5}`}></Box>
          <Box className={`${styles.particle} ${styles.particle6}`}></Box>
          <Box className={`${styles.particle} ${styles.particle7}`}></Box>
          <Box className={`${styles.particle} ${styles.particle8}`}></Box>

          {/* Floating Hardware Icons */}
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon1}`}>
            🔧
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon2}`}>
            ⚙️
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon3}`}>
            📱
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon4}`}>
            💻
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon5}`}>
            🔌
          </Box>
          <Box className={`${styles.floatingIcon} ${styles.floatingIcon6}`}>
            📡
          </Box>

          {/* Floating Shapes */}
          <Box className={`${styles.shape} ${styles.shape1}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape2}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape3}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>

          {/* Floating Lines */}
          <Box className={`${styles.line} ${styles.line1}`}></Box>
          <Box className={`${styles.line} ${styles.line2}`}></Box>

          {/* Circuit Board Pattern Dots */}
          <Box className={styles.circuitDots}>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
            <Box className={styles.circuitDot}></Box>
          </Box>
        </Box>

        <Container maxWidth="lg">
          <Box className={styles.heroContent}>
            <Typography
              variant="h1"
              className={styles.heroTitle}
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
              }}
            >
              Hardware <span className={styles.highlight}>Case Studies</span>
            </Typography>
            <Typography variant="h6" className={styles.heroSubtitle}>
              Pushing the boundaries of technology through innovative hardware
              solutions
            </Typography>

            {/* Scroll Indicator */}
            <Box className={styles.scrollIndicator}>
              <Box className={styles.scrollMouse}>
                <Box className={styles.scrollWheel}></Box>
              </Box>
              <Typography variant="caption" className={styles.scrollText}>
                Scroll to explore
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Intro Section */}
      <Box className={styles.introSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Box className={styles.introContent}>
                <Chip
                  label="💡 Our Philosophy"
                  className={styles.sectionChip}
                  size="small"
                />
                <Typography
                  variant="h2"
                  className={styles.sectionTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
                  }}
                >
                  <span className={styles.highlight}>Quality</span> & Innovative
                  Product Delivery
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  At Wavelength Consultancy Solutions,{" "}
                  <strong>
                    customer satisfaction based on quality and innovative
                    product delivery
                  </strong>{" "}
                  is very important.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We continue to push the boundaries of technology ensuring that
                  we are at the top of our game through our seasonal branded{" "}
                  <strong>Hardware and Software products</strong>.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  A business that is capable of differentiating their product
                  from other businesses in the same industry to a large extent
                  will enjoy <strong>profitable growth</strong>.
                </Typography>
                <Box className={styles.quoteBox}>
                  <Typography variant="body2" className={styles.quoteText}>
                    "Differentiation through innovation is the key to
                    sustainable growth."
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box className={styles.statsWrapper}>
                <Paper elevation={0} className={styles.statsPaper}>
                  <Grid container spacing={2}>
                    {statsData.map((stat, index) => (
                      <Grid size={{ xs: 6, sm: 6 }} key={index}>
                        <Box className={styles.statCard}>
                          <Box className={styles.statIcon}>{stat.icon}</Box>
                          <Typography
                            variant="h3"
                            className={styles.statNumber}
                          >
                            {stat.number}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={styles.statLabel}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sectors Section */}
      <Box className={styles.sectorsSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="🏢 Sectors We Serve"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Industry <span className={styles.highlight}>Sectors</span>
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {sectors.map((sector, index) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                <Box
                  className={styles.sectorCard}
                  sx={{
                    "&:hover": {
                      borderColor: sector.color,
                      boxShadow: `0 8px 32px ${sector.color}20`,
                    },
                  }}
                >
                  <Box
                    className={styles.sectorIcon}
                    sx={{ color: sector.color }}
                  >
                    {sector.icon}
                  </Box>
                  <Typography variant="body2" className={styles.sectorLabel}>
                    {sector.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Case Studies Section */}
      <Box className={styles.caseStudiesSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="📋 Case Studies"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Our <span className={styles.highlight}>Projects</span>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {caseStudies.map((study) => (
              <Grid size={{ xs: 12, sm: 6, md: 6 }} key={study.id}>
                <Card className={styles.caseCard}>
                  <Box className={styles.cardImageWrapper}>
                    <Box
                      component="img"
                      src={study.image}
                      alt={study.title}
                      className={styles.cardImage}
                    />
                    <Box className={styles.cardBadge}>
                      <Chip
                        label={study.category}
                        size="small"
                        className={styles.categoryBadge}
                        sx={{
                          background: `${study.color}20`,
                          color: study.color,
                        }}
                      />
                    </Box>
                    <Box
                      className={styles.cardIcon}
                      sx={{ color: study.color }}
                    >
                      {study.icon}
                    </Box>
                  </Box>
                  <CardContent className={styles.cardContent}>
                    <Typography variant="h5" className={styles.cardTitle}>
                      {study.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.cardDescription}
                    >
                      {study.description}
                    </Typography>
                    <Box className={styles.cardStats}>
                      {study.stats.map((stat, idx) => (
                        <Box key={idx} className={styles.cardStat}>
                          <Box
                            className={styles.cardStatIcon}
                            sx={{ color: study.color }}
                          >
                            {stat.icon}
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              className={styles.cardStatValue}
                            >
                              {stat.value}
                              {typeof stat.value === "number" ? "%" : ""}
                            </Typography>
                            <Typography
                              variant="caption"
                              className={styles.cardStatLabel}
                            >
                              {stat.label}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Box className={styles.cardFeatures}>
                      {study.features.slice(0, 3).map((feature, idx) => (
                        <Box key={idx} className={styles.cardFeature}>
                          <CheckCircle
                            className={styles.featureCheck}
                            sx={{ color: study.color }}
                          />
                          <Typography variant="caption">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Button
                      variant="contained"
                      className={styles.viewBtn}
                      onClick={() => handleOpenDialog(study)}
                      endIcon={<ArrowForward />}
                      fullWidth
                      sx={{ background: study.color }}
                    >
                      View Case Study
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box className={styles.ctaSection}>
        <Container maxWidth="lg">
          <Box className={styles.ctaContent}>
            <Typography variant="h3" className={styles.ctaTitle}>
              Ready to Build Your{" "}
              <span className={styles.highlight}>Hardware Solution</span>?
            </Typography>
            <Typography variant="body1" className={styles.ctaText}>
              Let's create innovative hardware products that will differentiate
              your business and drive growth.
            </Typography>
            <Box className={styles.ctaButtons}>
              <Button
                variant="contained"
                className={styles.ctaPrimaryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
              <Button
                variant="outlined"
                className={styles.ctaSecondaryBtn}
                startIcon={<Email />}
              >
                Email Us
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Case Study Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        className={styles.dialog}
      >
        {selectedStudy && (
          <>
            <DialogTitle className={styles.dialogTitle}>
              <Box className={styles.dialogHeader}>
                <Box
                  className={styles.dialogIcon}
                  sx={{
                    background: `${selectedStudy.color}15`,
                    color: selectedStudy.color,
                  }}
                >
                  {selectedStudy.icon}
                </Box>
                <Typography variant="h5" className={styles.dialogTitleText}>
                  {selectedStudy.title}
                </Typography>
                <IconButton
                  onClick={handleCloseDialog}
                  className={styles.dialogClose}
                >
                  <Close />
                </IconButton>
              </Box>
              <Chip
                label={selectedStudy.category}
                size="small"
                className={styles.dialogCategoryChip}
                sx={{
                  background: `${selectedStudy.color}20`,
                  color: selectedStudy.color,
                }}
              />
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="body1"
                    className={styles.dialogDescription}
                  >
                    {selectedStudy.description}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box className={styles.dialogSection}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      className={styles.dialogSectionTitle}
                    >
                      🎯 Challenge
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.dialogSectionText}
                    >
                      {selectedStudy.challenge}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box className={styles.dialogSection}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      className={styles.dialogSectionTitle}
                    >
                      💡 Solution
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.dialogSectionText}
                    >
                      {selectedStudy.solution}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box className={styles.dialogSection}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      className={styles.dialogSectionTitle}
                    >
                      📊 Results
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.dialogSectionText}
                    >
                      {selectedStudy.results}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider className={styles.dialogDivider} />
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    className={styles.dialogFeaturesTitle}
                  >
                    Key Features:
                  </Typography>
                  <Box className={styles.dialogFeaturesList}>
                    {selectedStudy.features.map((feature, index) => (
                      <Box key={index} className={styles.dialogFeature}>
                        <CheckCircle
                          className={styles.dialogFeatureCheck}
                          sx={{ color: selectedStudy.color }}
                        />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                className={styles.dialogCancelBtn}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className={styles.dialogInquiryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default HardwareCaseStudy;
